import { BooleanValue } from "common/genericOptions";
import { CadastroInstrumentoFinanceiroApiRequest, NegociacaoSecundaria, TipoPagamento, TipoSerie } from "common/genericTypes";
import moment from "moment";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { opcaoParaBoolean } from "utils/conversaoBooleanOption";
import { stripCpfCnpjFormatting } from "utils/validation";

export function useGerarRequest() {
  const caracteristicas = useSelector((state: RootState) => state.notaComercialCadastro.caracteristicas);
  const criteriosRemuneracao = useSelector((state: RootState) => state.notaComercialCadastro.criteriosRemuneracao);
  const eventosFinanceiros = useSelector((state: RootState) => state.notaComercialCadastro.eventos);
  const uploadDocumentos = useSelector((state: RootState) => state.notaComercialCadastro.uploadDocumentos);
  const emissor = useSelector((state: RootState) => state.notaComercialCadastro.emissor);

  const isTipoPagamentoSacOrPrice = criteriosRemuneracao.tipoPagamento && criteriosRemuneracao.tipoPagamento !== TipoPagamento.PagamentoFinal;

  const result: CadastroInstrumentoFinanceiroApiRequest = {
    draftId: caracteristicas.draftId ?? undefined,
    emissorId: emissor?.id ?? undefined,
    codigoExterno: emissor?.codigoExterno ?? undefined,
    numeroEmissao: caracteristicas.numeroEmissao,
    numeroSerie: caracteristicas.numeroSerie,
    tipoSerie: caracteristicas.tipoSerie.value as TipoSerie,
    regimeFiduciario: caracteristicas.regimeFiduciario.value === BooleanValue.Yes,
    distribuicaoPublica: caracteristicas.distribuicaoPublica.value === BooleanValue.Yes,
    negociacaoSecundariaPor: caracteristicas.negociacaoSecundariaPor.value as NegociacaoSecundaria,
    depositariaEmissao: caracteristicas.depositariaEmissao.value,
    utilizacaoAnuncioInicioDistribuicao:
      caracteristicas.utilizacaoAnuncioInicioDistribuicao.value === BooleanValue.Yes,
    ufLocalEmissao: caracteristicas.ufLocalEmissao.value,
    localEmissao: caracteristicas.localEmissao,
    classificadoraRisco1: caracteristicas.classificadoraRisco1,
    rating1: caracteristicas.rating1,
    classificadoraRisco2: caracteristicas.classificadoraRisco2,
    rating2: caracteristicas.rating2,
    ufLocalPagamento: caracteristicas.ufLocalPagamento.value,
    localPagamento: caracteristicas.localPagamento,
    utilizacaoFaculdadeParagrafo3Art7: false,
    nomeResponsavelPelasInformacoesDRI: caracteristicas.nomeResponsavelPelasInformacoesDRI,
    emailResponsavelPelasInformacoesDRI: caracteristicas.emailResponsavelPelasInformacoesDRI,
    bancoLiquidanteEmissor: caracteristicas.bancoLiquidanteEmissor,
    cnpjBancoLiquidanteEmissor: stripCpfCnpjFormatting(caracteristicas.cnpjBancoLiquidanteEmissor),
    codigoBancoContaCorrenteVinculadaEmissao: caracteristicas.codigoBancoContaCorrenteVinculadaEmissao,
    numeroAgenciaContaCorrenteVinculadaEmissao: caracteristicas.numeroAgenciaContaCorrenteVinculadaEmissao,
    numeroContaCorrenteVinculadaEmissao: caracteristicas.numeroContaCorrenteVinculadaEmissao,
    tipoLiquidacao: caracteristicas.tipoLiquidacao.value,
    descricaoAdicional: caracteristicas.descricaoAdicional,
    garantias: caracteristicas.garantias,
    descricaoAdicionalGarantias: caracteristicas.descricaoAdicionalGarantias,
    chaveExterna: caracteristicas.chaveExterna,
    isin: caracteristicas.isin,
    coobrigacao: caracteristicas.coobrigacao.value === BooleanValue.Yes,
    naturezaAgenteFiduciario: caracteristicas.naturezaAgenteFiduciario.value,
    razaoSocialAgenteFiduciario: caracteristicas.razaoSocialAgenteFiduciario,
    cpfCnpjAgenteFiduciario: stripCpfCnpjFormatting(caracteristicas.cpfCnpjAgenteFiduciario),
    tipoDistribuicaoPublica: caracteristicas.tipoDistribuicaoPublica.value,
    contraparteRecebedora: caracteristicas.contraparteRecebedora.value,
    dataEncerramentoDistribuicao: caracteristicas.dataEncerramentoDistribuicao
      ? moment(caracteristicas.dataEncerramentoDistribuicao).format('YYYY-MM-DD HH:mm:ss')
      : undefined,
    coordenadoresIds: caracteristicas.coordenadoresIds,
    coordenadorLiderId: caracteristicas.coordenadorLiderId ?? undefined,
    razaoSocialEscriturador: caracteristicas.razaoSocialEscriturador,
    cnpjEscriturador: stripCpfCnpjFormatting(caracteristicas.cnpjEscriturador),
    cnpjEmissor: caracteristicas.cnpjEmissor ? stripCpfCnpjFormatting(caracteristicas.cnpjEmissor) : undefined,
    custodiantes: criteriosRemuneracao.custodiantes ?? [],
    criteriosRemuneracao: {
      valorNominalUnitarioEmissao: criteriosRemuneracao.valorNominalUnitarioEmissao,
      quantidadeEmitida: criteriosRemuneracao.quantidadeEmitida,
      volumeEmissao: criteriosRemuneracao.volumeEmissao,
      moedaEmissao: criteriosRemuneracao.moedaEmissao,
      dataEmissao: criteriosRemuneracao.dataEmissao
        ? moment(criteriosRemuneracao.dataEmissao).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
      dataVencimento: criteriosRemuneracao.dataVencimento
        ? moment(criteriosRemuneracao.dataVencimento).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
      cotacaoInicial: criteriosRemuneracao.cotacaoInicial,
      dataInicioRentabilidade: criteriosRemuneracao.dataInicioRentabilidade
        ? moment(criteriosRemuneracao.dataInicioRentabilidade).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
      indexador: criteriosRemuneracao.remuneracao.indexador,
      indexadorPorcentagem: criteriosRemuneracao.remuneracao.indexadorPorcentagem,
      defasagemFixing: criteriosRemuneracao.remuneracao.defasagemFixing,
      periodicidadeCorrecao: criteriosRemuneracao.remuneracao.periodicidadeCorrecao,
      proRata: criteriosRemuneracao.remuneracao.proRata,
      corrigePeriodoFinal: criteriosRemuneracao.remuneracao.corrigePeriodoFinal,
      diaAniversario: criteriosRemuneracao.remuneracao.diaAniversario,
      mesAniversario: criteriosRemuneracao.remuneracao.mesAniversario,
      tipoProjecao: criteriosRemuneracao.remuneracao.tipoProjecao,
      taxaJurosFixoSpread: criteriosRemuneracao.remuneracao.taxaJurosFixoSpreadPorcentagem,
      convencaoJurosFixos: criteriosRemuneracao.remuneracao.convencao,
      custoEmissao: {
        custoTotal: criteriosRemuneracao?.custoEmissao?.custoTotal ?? undefined,
        feeLaqusBruto: criteriosRemuneracao?.custoEmissao?.feeLaqusBruto,
        feeLaqusLiquido: criteriosRemuneracao?.custoEmissao?.feeLaqusLiquido ?? undefined,
        feeRepasse: criteriosRemuneracao.custoEmissao?.feeRepasse ?? undefined
      },
    },
    amortizacaoSobre: eventosFinanceiros.percentualAmortizacaoSobre,
    amortizacaoPassivelAoTermoSecuritizacao: eventosFinanceiros.percentualAmortizacaoPassivelAlteracao,
    amortizacoes: eventosFinanceiros.amortizacoes,
    pagamentoJuros: {
      datas: eventosFinanceiros.datasPagamentoJuros
        ? eventosFinanceiros.datasPagamentoJuros.filter((_) => !!_).map((d) => moment(d).format('YYYY-MM-DD HH:mm:ss'))
        : [],
    },
    incorporacaoJuros: {
      datas: eventosFinanceiros.datasIncorporacaoJuros
        ? eventosFinanceiros.datasIncorporacaoJuros.filter((_) => !!_).map((d) => moment(d).format('YYYY-MM-DD HH:mm:ss'))
        : [],
    },
    possuiResgateAntecipado: eventosFinanceiros.resgateAntecipadoFacultativo,
    vasosComunicantes: opcaoParaBoolean(caracteristicas.possuiVasosComunicantes),
    tipoPagamento: criteriosRemuneracao.tipoPagamento ? criteriosRemuneracao.tipoPagamento : undefined,
    convencaoCalendarioPagamento: isTipoPagamentoSacOrPrice ? criteriosRemuneracao.convencaoCalendarioPagamento : undefined,
    primeiraParcelaAmortizacao: isTipoPagamentoSacOrPrice
      ? moment(criteriosRemuneracao.primeiraParcelaAmortizacao).format('YYYY-MM-DD HH:mm:ss')
      : undefined,
    primeiraParcelaJuros: isTipoPagamentoSacOrPrice
      ? moment(criteriosRemuneracao.primeiraParcelaJuros).format('YYYY-MM-DD HH:mm:ss')
      : undefined,
    periodicidadeAmortizacao: isTipoPagamentoSacOrPrice ? criteriosRemuneracao.periodicidadeAmortizacao : undefined,
    periodicidadeJuros: isTipoPagamentoSacOrPrice ? criteriosRemuneracao.periodicidadeJuros : undefined,
    documents:
      uploadDocumentos?.documents
        .filter((doc) => !!doc.remoteFileInfo)
        .map((doc) => ({
          id: doc.remoteFileInfo!.id,
          address: doc.remoteFileInfo!.address,
          emissionType: doc.type,
          extension: doc.remoteFileInfo!.extension,
          fileName: doc.remoteFileInfo!.fileName,
          size: doc.remoteFileInfo!.size,
        })) ?? [],
  }

  return result;
}